/** `bump-arcade/sewer-1983` — the bump-arcade family template authored from
 * the stored 1983 Mario concept: a single-screen sewer arena with wrapping
 * edges, four corner pipes (enemies emerge from the upper pipes, walk down the
 * tiers, enter the lower pipes and recirculate), platforms bumped from below
 * to flip pests (Shellcreeper: one hit; Sidestepper: enrages, then flips on
 * the second; Fighter Fly: hops, flips only while grounded), touch-to-kick
 * with an 800/1600/2400/3200 chain, recovery that returns faster, a
 * three-use POW block that refills after a Bonus Phase, coins dropped by
 * defeats that travel into the pipes, Bonus Phases of ten coins on a clock,
 * 22 phase patterns looping with escalating speed, lives and score; clearing
 * six phases wins (a decisive end; the count is tunable).
 *
 * Constants follow the live game (`1983-mario` v10 sim) where it matched the
 * concept; pipes, a working POW and the two-hit Sidestepper follow the
 * concept where the live game had drifted. */
import type { GameSpecV1, ModuleUseV1 } from '../spec.js';
import type { DirectorConfig, PhaseDef, SpawnGroup } from '../modules/director.js';
import type { TemplateV1 } from './template.js';

const FLOOR = 8.3, LOW = 6.3, MID = 4.3, TOP = 2.3;
const W = 16;

/** Arcade pattern rule (live sim): pests per phase min(2+⌊(p−1)/3⌋, 6);
 * Sidesteppers from pattern 3 on odd slots, Fighter Flies from pattern 6 on
 * every third slot; Bonus Phases at 3, 8, then every 7. */
export function sewerPhaseTable(patterns = 22): PhaseDef[] {
  const phases: PhaseDef[] = [];
  for (let p = 1; p <= patterns; p++) {
    const bonus = p === 3 || p === 8 || (p > 8 && (p - 8) % 7 === 0);
    if (bonus) {
      const coins: SpawnGroup[] = [];
      const spots: Array<[number, number]> = [[2, LOW], [4.5, LOW], [11.5, LOW], [14, LOW], [5, MID], [8, MID], [11, MID], [2.5, TOP], [13.5, TOP], [8, FLOOR]];
      for (const [x, y] of spots) coins.push({ archetype: 'coin', count: 1, via: 'point', x, y, dir: 0, delayTicks: 0, everyTicks: 0, jitterTicks: 0 });
      phases.push({ kind: 'bonus', spawns: coins, durationTicks: 600, clearBonus: 5000, layout: [] });
      continue;
    }
    const count = Math.min(2 + Math.floor((p - 1) / 3), 6);
    const spawns: SpawnGroup[] = [];
    for (let i = 0; i < count; i++) {
      const kind = p >= 6 && i % 3 === 2 ? 'fighter-fly' : p >= 3 && i % 2 === 1 ? 'sidestepper' : 'shellcreeper';
      spawns.push({ archetype: kind, count: 1, via: 'pipe', x: 0, y: 0, dir: 1, delayTicks: 30 + i * 120, everyTicks: 0, jitterTicks: 45 });
    }
    phases.push({ kind: 'clear', spawns, durationTicks: 0, clearBonus: 0, layout: [] });
  }
  return phases;
}

const pest = (role: string, w: number, h: number, palette: number, speed: number, extra: {
  hitsToFlip: number; hop?: boolean; facing?: 'front';
}): GameSpecV1['archetypes'][string] => ({
  role, tags: ['enemy', 'pipe-traveller', 'required'], size: { w, h },
  render: { palette, depth: 16, anchor: 'bottom', ...(extra.facing ? { facing: extra.facing } : {}) },
  modules: [
    { use: 'ai.walker', config: { speed, gravity: 27, maxFall: 10, edge: 'fall', footSpan: 0.2, turnPauseTicks: 0, speedScalar: 'enemy.speed', animate: true, airborneState: extra.hop === true } },
    ...(extra.hop ? [{ use: 'ai.hopper', config: { firstTicks: 90, everyTicks: 75, hopSpeed: 6.5 } }] : []),
    { use: 'rule.vulnerableWindow', config: { hitsToFlip: extra.hitsToFlip, flipTicks: 150, requireGrounded: true, angrySpeedMul: 1.7,
      recoverSpeedMul: 1.55, flipScore: 10, defeatTicks: 24, hitSignals: ['hit-from-below', 'pow'], dropArchetype: 'coin', dropSpeed: 1.4, flipPop: 3, recoverPop: 4,
      enrageState: extra.hitsToFlip > 1 } },
  ],
});

const director: DirectorConfig = {
  phases: sewerPhaseTable(), loopFrom: 0, interPhaseTicks: 60, requiredTag: 'required', collectTag: 'coin', sweepTag: 'coin',
};

const globals: ModuleUseV1[] = [
  { use: 'world.wrapX', config: {} },
  { use: 'world.layout', config: { layout: [
    { archetype: 'platform', x: 0, y: FLOOR, w: W, h: 0.7 },
    { archetype: 'platform', x: 0, y: LOW, w: 5.6, h: 0.4 },
    { archetype: 'platform', x: 10.4, y: LOW, w: 5.6, h: 0.4 },
    { archetype: 'platform', x: 3.4, y: MID, w: 9.2, h: 0.4 },
    { archetype: 'platform', x: 0, y: TOP, w: 6, h: 0.4 },
    { archetype: 'platform', x: 10, y: TOP, w: 6, h: 0.4 },
    { archetype: 'pow-block', x: 7.45, y: 5.95, w: 1.1, h: 0.75 },
  ] } },
  { use: 'world.pipes', config: { archetype: 'pipe', travellerTag: 'pipe-traveller', vanishTag: 'coin', emergeTicks: 20, spacingTicks: 60, mouth: 0.9,
    pipes: [
      { id: 'upper-left', x: 0.6, y: TOP, side: 'left', role: 'entry' },
      { id: 'upper-right', x: 15.4, y: TOP, side: 'right', role: 'entry' },
      { id: 'lower-left', x: 0.6, y: FLOOR, side: 'left', role: 'exit' },
      { id: 'lower-right', x: 15.4, y: FLOOR, side: 'right', role: 'exit' },
    ] } },
  { use: 'rule.bumpFromBelow', config: { bumperTag: 'player', targetTags: ['enemy', 'coin'], reach: 0.95, partnerPopSpeed: 4.5 } },
  { use: 'rule.contactDamage', config: { attackerTag: 'enemy', victimTag: 'player', exceptAttackerTags: ['vulnerable', 'defeated'], openingSafeTicks: 90, consumeAttacker: false } },
  { use: 'flow.director', config: director },
  { use: 'flow.escalation', config: { curves: [{ scalar: 'enemy.speed', index: 'phase.loop', kind: 'linear', base: 1, rate: 0.15, min: 1, max: 1.9 }] } },
  // A decisive end (the platform's revision-3 contract): clearing six phases —
  // through the first two Bonus Phases — wins; losing every life loses.
  { use: 'flow.objective', config: { win: 'phases-cleared', count: 6, milestone: 0, lose: 'players-out', requiredTag: 'required', collectTag: 'coin', celebrate: false } },
];

export const SEWER_1983_SPEC: GameSpecV1 = {
  schema: 'game-spec@1', kit: 'compose@1',
  title: '1983 Sewer Bump',
  summary: 'a single-screen arcade platform game in a sewer arena',
  world: { w: W, h: 9, orientation: 'landscape' },
  players: { archetypes: ['mario'], spawns: [{ x: 8, y: FLOOR, facing: -1 }] },
  archetypes: {
    mario: {
      role: 'the player plumber who runs, jumps, bumps platforms from below and kicks flipped pests',
      tags: ['player'], size: { w: 0.8, h: 1.2 }, render: { palette: 3, depth: 20, anchor: 'bottom', shadow: true },
      modules: [
        { use: 'body.platformer', config: { run: 'momentum', maxSpeed: 3.6, accel: 90, airAccel: 18, friction: 13, iceFriction: 3.5,
          gravity: 27, jumpSpeed: 12.5, maxFall: 13, coyoteTicks: 3, jumpCut: 1, footSpan: 0.68, variableJump: false } },
        { use: 'rule.kicker', config: { chainScores: [800, 1600, 2400, 3200], chainTicks: 75, poseTicks: 9 } },
        { use: 'core.vitals', config: { lives: 3, hurtTicks: 24, respawnTicks: 60, invulnTicks: 75 } },
      ],
    },
    shellcreeper: pest('a basic turtle-like sewer pest flipped by one bump from below', 0.85, 0.7, 4, 1.0, { hitsToFlip: 1 }),
    sidestepper: pest('a crab-like sewer pest that enrages on the first bump and flips on the second', 1.0, 0.72, 3, 1.25, { hitsToFlip: 2, facing: 'front' }),
    'fighter-fly': pest('a hopping winged sewer pest flipped only while touching a platform', 0.85, 0.72, 6, 1.0, { hitsToFlip: 1, hop: true }),
    coin: {
      role: 'a gold coin that travels along the platforms into the pipes and scores when touched',
      tags: ['coin', 'pipe-traveller'], size: { w: 0.52, h: 0.52 }, render: { palette: 5, depth: 14, anchor: 'bottom' },
      modules: [
        { use: 'ai.walker', config: { speed: 1.4, gravity: 27, maxFall: 10, edge: 'fall', footSpan: 0.2, turnPauseTicks: 0, animate: false, airborneState: false } },
        { use: 'rule.pickup', config: { collectorTag: 'player', score: 800, collectOnHitFromBelow: true } },
      ],
    },
    'pow-block': {
      role: 'the POW block struck from below to shake the whole arena',
      tags: ['block'], size: { w: 1.1, h: 0.75 }, render: { palette: 2, depth: 6, anchor: 'center', surface: true, label: 'POW' },
      modules: [
        { use: 'body.surface', config: { mode: 'solid', bumpable: false } },
        { use: 'rule.powBlock', config: { uses: 3, targetTag: 'enemy', clearTag: 'hazard', refillOn: 'bonus-cleared', shrink: true } },
      ],
    },
    platform: {
      role: 'a blue-green sewer girder platform bumped from below',
      tags: ['scenery'], size: { w: 4, h: 0.4 }, render: { palette: 1, depth: 2, anchor: 'center', surface: true },
      modules: [{ use: 'body.surface', config: { mode: 'solid', bumpable: true } }],
    },
    pipe: {
      role: 'a large corner sewer pipe where pests and coins enter and leave',
      tags: ['scenery'], size: { w: 1.2, h: 1.2 }, render: { palette: 2, depth: 18, anchor: 'bottom' },
      modules: [{ use: 'body.static', config: {} }],
    },
  },
  globals,
  hud: [{ from: 'score', field: 'score' }, { from: 'core.vitals', field: 'lives' }, { from: 'flow.director', field: 'phase' },
    { from: 'flow.director', field: 'remaining' }, { from: 'rule.powBlock', field: 'uses' }, { from: 'flow.director', field: 'clock' }],
  input: { profile: 'run-jump', labels: { a: 'JUMP' } },
  presentation: {
    projection: 'side', backgroundRole: 'the black sewer chamber behind the single-screen arena',
    palette: ['#000000', '#00a8a8', '#0050d8', '#e83030', '#35b84a', '#ffd43b', '#f4f4f4', '#8b4a2b'],
    backgroundPaletteIndex: 0,
  },
};

export const SEWER_1983_TEMPLATE: TemplateV1 = {
  id: 'bump-arcade/sewer-1983',
  family: 'bump-arcade',
  title: '1983 sewer bump arcade',
  pitch: 'Single-screen arena: bump platforms from below to flip pests, kick them before they recover, clear phases.',
  disclosure: {
    coreVerbs: ['run', 'jump', 'bump'],
    mechanics: [
      'A single-screen arena of platform tiers whose left and right edges wrap around.',
      'Pests emerge from the two upper corner pipes, walk down the tiers and re-enter play through the lower pipes.',
      'Jumping into a platform from below flips the pest standing on it; touching a flipped pest kicks it away for a rising score chain.',
      'Three pest kinds: one flips on one bump, one enrages on the first bump and flips on the second, one hops and flips only while grounded.',
      'A flipped pest recovers after a few seconds, faster and angrier; defeated pests drop coins that travel into the pipes.',
      'A POW block hit from below flips every grounded pest; it has a few uses and refills after a bonus phase.',
      'Phases loop through 22 patterns with escalating speed; bonus phases ask you to grab ten coins against the clock.',
      'Touching an upright pest costs a life; lose every life to end the game.',
    ],
    controls: 'Move left or right; jump to bump the platform above you. Touch a flipped pest to kick it.',
    winCondition: 'Clear six phases (two of them Bonus Phases) to win; losing every life ends the game.',
  },
  music: { mood: 'bouncy frantic sewer arcade', genre: 'retro arcade chiptune', tempoBpm: 150,
    instrumentation: ['square lead', 'pulse arpeggio', 'triangle bass', 'noise drums'] },
  spec: SEWER_1983_SPEC,
  tunables: [
    { path: '/archetypes/mario/modules/body.platformer/maxSpeed', min: 2.6, max: 5 },
    { path: '/archetypes/mario/modules/body.platformer/jumpSpeed', min: 11.5, max: 14 },
    { path: '/archetypes/mario/modules/core.vitals/lives', min: 1, max: 5, integer: true },
    { path: '/archetypes/shellcreeper/modules/ai.walker/speed', min: 0.6, max: 1.8 },
    { path: '/archetypes/sidestepper/modules/ai.walker/speed', min: 0.8, max: 2 },
    { path: '/archetypes/fighter-fly/modules/ai.walker/speed', min: 0.6, max: 1.8 },
    { path: '/archetypes/shellcreeper/modules/rule.vulnerableWindow/flipTicks', min: 90, max: 240, integer: true },
    { path: '/archetypes/sidestepper/modules/rule.vulnerableWindow/flipTicks', min: 90, max: 240, integer: true },
    { path: '/archetypes/fighter-fly/modules/rule.vulnerableWindow/flipTicks', min: 90, max: 240, integer: true },
    { path: '/archetypes/pow-block/modules/rule.powBlock/uses', min: 1, max: 5, integer: true },
    { path: '/globals/flow.escalation/curves/0/rate', min: 0, max: 0.3 },
    { path: '/globals/flow.director/interPhaseTicks', min: 30, max: 150, integer: true },
    { path: '/globals/flow.objective/count', min: 3, max: 8, integer: true },
  ],
  contentSlots: [
    { path: '/title', maxLength: 80 },
    { path: '/summary', maxLength: 160 },
    { path: '/archetypes/*/role', maxLength: 120 },
    { path: '/presentation/backgroundRole', maxLength: 120 },
  ],
  requirements: { events: ['bump', 'flip', 'enrage', 'kick', 'defeat', 'pow', 'pipe-emerge', 'collect', 'phase-clear', 'bonus-start',
    'defeat:shellcreeper', 'defeat:sidestepper', 'defeat:fighter-fly', 'enrage:sidestepper'] },
  signatureModules: ['rule.bumpFromBelow', 'rule.vulnerableWindow', 'rule.kicker', 'rule.powBlock', 'world.pipes', 'flow.director'],
  // The seat spawns under the POW block: a jump from the opening position
  // strikes it from below. Kicking needs a flipped pest, which the solver
  // proof covers (required `kick` events), not a fixed native script.
  verbProofs: [
    { verb: 'run', input: 'right', ms: 500, expect: { kind: 'state-change', path: 'seats.0.x' } },
    { verb: 'jump', input: 'a', ms: 300, expect: { kind: 'event', event: 'jump' } },
    { verb: 'bump', input: 'a', ms: 500, expect: { kind: 'event', event: 'pow' } },
  ],
};
